import { VisitDiagnosis } from '@dreryk/egabinet-visit-frontend-api';

import { TranslateFunction } from '@dreryk/react-common';

import {
   createReimbursementIndicationValidChecker,
   createStatutorilyDesignatedNonFullEntitlementIndicationValidChecker,
   hasEligibleReimbursementCode,
   isStatutorilyDesignatedNonFullEntitlementsOnly,
} from '~libs.shared.prescription';

import { DraftPrescriptionField, DraftPrescriptionsFieldArray } from '../../DraftPrescriptionsForm.types';
import { draftPrescriptionsTranslationKeys } from '../../../../../../translations';

export const filterPrescriptionsWithoutValidReimbursementIndications = (
   prescriptions: DraftPrescriptionsFieldArray,
   visitDiagnoses: VisitDiagnosis[]
): DraftPrescriptionsFieldArray => {
   return prescriptions.filter((prescription) => {
      const { reimbursement, additionalEntitlements } = prescription.data;
      const reimbursementIndications = reimbursement?.reimbursementIndication;

      if (!reimbursementIndications || !hasEligibleReimbursementCode(reimbursement?.code)) {
         return false;
      }
      return !reimbursementIndications.some(
         createReimbursementIndicationValidChecker(visitDiagnoses, additionalEntitlements ?? [])
      );
   });
};

export const filterPrescriptionsWithoutValidReimbursementIndicationsForStatutorilyDesignatedNonFullEntitlements = (
   prescriptions: DraftPrescriptionsFieldArray,
   visitDiagnoses: VisitDiagnosis[]
): DraftPrescriptionsFieldArray => {
   return prescriptions.filter((prescription) => {
      const { reimbursement, additionalEntitlements } = prescription.data;
      const reimbursementIndications = reimbursement?.reimbursementIndication;

      if (
         reimbursementIndications &&
         additionalEntitlements &&
         hasEligibleReimbursementCode(reimbursement?.code) &&
         isStatutorilyDesignatedNonFullEntitlementsOnly(additionalEntitlements)
      ) {
         return !reimbursementIndications.some(
            createStatutorilyDesignatedNonFullEntitlementIndicationValidChecker(visitDiagnoses)
         );
      }

      return false;
   });
};

export const formatReimbursement = (prescription: DraftPrescriptionField, translate: TranslateFunction): string => {
   const { additionalEntitlements, reimbursement } = prescription.data;
   if (!hasEligibleReimbursementCode(reimbursement?.code)) {
      return '';
   }

   const reimbursementPrefix = translate(draftPrescriptionsTranslationKeys.reimbursements[reimbursement.code]);

   if (additionalEntitlements && isStatutorilyDesignatedNonFullEntitlementsOnly(additionalEntitlements)) {
      const conjunction = translate(
         draftPrescriptionsTranslationKeys.reimbursements.reimbursementWithEntitlements.conjunction
      );

      const entitlementsSuffix = additionalEntitlements
         .map((entitlement) =>
            translate(
               draftPrescriptionsTranslationKeys.reimbursements.reimbursementWithEntitlements[
                  entitlement.additionalEntitlementType
               ]
            )
         )
         .join(conjunction);

      return `${reimbursementPrefix}${conjunction}${entitlementsSuffix}`;
   }

   return reimbursementPrefix;
};
